import type { ApiErrorBody, Me } from '@aitoken/contracts';
import { queryClient } from './query-client';
import { getLocale } from '@/i18n/runtime';
import { errorStateT } from '@/i18n/messages/errorState';

/** 统一的接口错误：code / message / status / request_id 来自服务端错误体（ApiErrorBody）。 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly requestId: string | null;
  readonly param: string | undefined;
  readonly retryable: boolean;

  constructor(init: {
    code: string;
    message: string;
    status: number;
    requestId: string | null;
    param?: string;
    retryable?: boolean;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.requestId = init.requestId;
    this.param = init.param;
    this.retryable = init.retryable ?? false;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export const ME_QUERY_KEY = ['me'] as const;

type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;

/** 由路由层注册：受保护页面收到 401 时跳转登录并带回当前路径。 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null) {
  onUnauthorized = handler;
}

type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Query;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** 401 时不触发跳转（例如 /portal/me 探测登录态） */
  silent401?: boolean;
}

export function buildUrl(path: string, query?: Query): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

let csrfInflight: Promise<string | null> | null = null;

async function getCsrfToken(forceRefresh = false): Promise<string | null> {
  if (!forceRefresh) {
    const cached = queryClient.getQueryData<Me | null>(ME_QUERY_KEY);
    if (cached?.csrf_token) return cached.csrf_token;
  }
  csrfInflight ??= fetchMe()
    .then((me) => {
      queryClient.setQueryData(ME_QUERY_KEY, me);
      return me?.csrf_token ?? null;
    })
    .finally(() => {
      csrfInflight = null;
    });
  return csrfInflight;
}

async function parseError(res: Response): Promise<ApiError> {
  const headerRequestId = res.headers.get('X-Request-Id');
  let body: Partial<ApiErrorBody> | null = null;
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    body = null;
  }
  const err = body?.error;
  if (err && typeof err.code === 'string') {
    return new ApiError({
      code: err.code,
      message: err.message || defaultMessage(res.status),
      status: res.status,
      requestId: err.request_id ?? headerRequestId,
      param: err.param,
      retryable: err.retryable,
    });
  }
  return new ApiError({
    code: res.status >= 500 ? 'internal_error' : 'http_error',
    message: defaultMessage(res.status),
    status: res.status,
    requestId: headerRequestId,
  });
}

function defaultMessage(status: number): string {
  if (status === 401) return errorStateT('http.401');
  if (status === 403) return errorStateT('http.403');
  if (status === 404) return errorStateT('http.404');
  if (status === 429) return errorStateT('http.429');
  if (status >= 500) return errorStateT('http.5xx');
  return errorStateT('http.other', { status });
}

async function rawRequest(path: string, opts: RequestOptions, csrf: string | null): Promise<Response> {
  const method = opts.method ?? 'GET';
  // 服务端按 Accept-Language 返回对应语言的错误信息
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Accept-Language': getLocale(),
    ...opts.headers,
  };
  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  if (method !== 'GET' && csrf) headers['X-CSRF-Token'] = csrf;
  try {
    return await fetch(buildUrl(path, opts.query), {
      method,
      headers,
      body,
      credentials: 'include',
      signal: opts.signal,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError({
      code: 'network_error',
      message: errorStateT('network'),
      status: 0,
      requestId: null,
    });
  }
}

const NO_CSRF_PATHS = [
  '/portal/auth/email-code',
  '/portal/auth/login',
  '/portal/auth/login-code',
  '/portal/auth/login-mfa',
  '/portal/auth/register',
  '/portal/auth/forgot-password',
  '/portal/auth/reset-password',
];

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const needsCsrf = method !== 'GET' && !NO_CSRF_PATHS.includes(path);
  let csrf = needsCsrf ? await getCsrfToken() : null;
  let res = await rawRequest(path, opts, csrf);

  // CSRF 令牌失效（会话轮换后）：刷新一次再重试
  if (res.status === 403 && needsCsrf) {
    const err = await parseError(res.clone());
    if (err.code === 'csrf_failed') {
      csrf = await getCsrfToken(true);
      res = await rawRequest(path, opts, csrf);
    }
  }

  if (!res.ok) {
    const err = await parseError(res);
    if (res.status === 401 && !opts.silent401) {
      queryClient.setQueryData(ME_QUERY_KEY, null);
      onUnauthorized?.();
    }
    throw err;
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(path: string, query?: Query, opts?: Omit<RequestOptions, 'method' | 'query'>) =>
    request<T>(path, { ...opts, method: 'GET', query }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'POST', body: body ?? {} }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'PATCH', body: body ?? {} }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'PUT', body: body ?? {} }),
  delete: <T>(path: string, opts?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...opts, method: 'DELETE' }),
};

/** 登录态探测：未登录返回 null，不触发跳转。 */
export async function fetchMe(): Promise<Me | null> {
  try {
    return await request<Me>('/portal/me', { silent401: true });
  } catch (error) {
    if (isApiError(error) && error.status === 401) return null;
    throw error;
  }
}

/** 错误信息的统一文案（toast 等）。 */
export function describeError(error: unknown): { title: string; requestId: string | null } {
  if (isApiError(error)) return { title: error.message, requestId: error.requestId };
  if (error instanceof Error) return { title: error.message, requestId: null };
  return { title: errorStateT('unknown'), requestId: null };
}
